import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { canUseStaffPage, getStaffLandingPath, VIEWER_RESTRICTED_PAGES } from "@/lib/page-permissions";
import StaffLanding from "@/components/StaffLanding";
import NoStaffPages from "@/pages/NoStaffPages";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import StaffLayout from "@/components/StaffLayout";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import PendingUpdates from "@/pages/PendingUpdates";
import Reports from "@/pages/Reports";
import SharingTree from "@/pages/SharingTree";
import Settings from "@/pages/Settings";
import ImportCSV from "@/pages/ImportCSV";
import AuditLog from "@/pages/AuditLog";
import StaffUsers from "@/pages/StaffUsers";
import StaffEditPage from "@/pages/StaffEditPage";
import ClientLogin from "@/pages/ClientLogin";
import ClientVerify from "@/pages/ClientVerify";
import ClientSetup from "@/pages/ClientSetup";
import ClientPortal from "@/pages/ClientPortal";
import StaffSetup from "@/pages/StaffSetup";
import Profile from "@/pages/Profile";
import SaturdayQueue from "@/pages/SaturdayQueue";
import KioskRoute from "@/components/KioskRoute";
import CompareClients from "@/pages/CompareClients";
import HelpPage from "@/pages/HelpPage";
import AddressCheckPolicy from "@/pages/AddressCheckPolicy";

function StaffOnly({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user || user.user_type !== "staff") return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function StaffWriterOnly({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (user?.role === "viewer" && VIEWER_RESTRICTED_PAGES.some(slug => location.pathname === `/staff/${slug}` || location.pathname.startsWith(`/staff/${slug}/`))) {
    return <Navigate to={getStaffLandingPath(user)} state={{ accessDenied: true }} replace />;
  }
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.role !== "admin") return <Navigate to={getStaffLandingPath(user)} state={{ accessDenied: true }} replace />;
  return children;
}

function PageAccessGuard({ slug, children }) {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return null;
  if (!canUseStaffPage(user, slug)) {
    return <Navigate to={getStaffLandingPath(user)} replace
      state={{ accessDenied: true, deniedPage: slug }} />;
  }
  return children;
}

function ClientOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.user_type !== "client") return <Navigate to="/portal/login" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/address-check-policy" element={<AddressCheckPolicy />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/staff-setup" element={<StaffSetup />} />
          <Route path="/portal/login" element={<ClientLogin />} />
          <Route path="/portal/verify" element={<ClientVerify />} />
          <Route path="/portal/setup" element={<ClientSetup />} />
          <Route path="/portal" element={<ClientOnly><ClientPortal /></ClientOnly>} />

          <Route path="/staff/kiosk" element={<KioskRoute />} />
          <Route path="/staff/no-pages" element={<StaffOnly><NoStaffPages /></StaffOnly>} />

          <Route path="/staff" element={<StaffOnly><StaffWriterOnly><StaffLayout /></StaffWriterOnly></StaffOnly>}>
            <Route index element={<StaffLanding />} />
            <Route path="dashboard" element={<PageAccessGuard slug="dashboard"><Dashboard /></PageAccessGuard>} />
            <Route path="clients" element={<PageAccessGuard slug="clients"><Clients /></PageAccessGuard>} />
            <Route path="clients/compare" element={<PageAccessGuard slug="clients"><CompareClients /></PageAccessGuard>} />
            <Route path="clients/:id" element={<PageAccessGuard slug="clients"><ClientDetail /></PageAccessGuard>} />
            <Route path="pending" element={<PageAccessGuard slug="pending"><PendingUpdates /></PageAccessGuard>} />
            <Route path="reports" element={<PageAccessGuard slug="reports"><Reports /></PageAccessGuard>} />
            <Route path="sharing-tree" element={<PageAccessGuard slug="sharing-tree"><SharingTree /></PageAccessGuard>} />
            <Route path="settings" element={<PageAccessGuard slug="settings"><Settings /></PageAccessGuard>} />
            <Route path="queue" element={<PageAccessGuard slug="queue"><SaturdayQueue /></PageAccessGuard>} />
            <Route path="import" element={<PageAccessGuard slug="import"><ImportCSV /></PageAccessGuard>} />
            <Route path="audit" element={<PageAccessGuard slug="audit"><AuditLog /></PageAccessGuard>} />
            <Route path="users" element={<AdminOnly><StaffUsers /></AdminOnly>} />
            <Route path="users/:id" element={<AdminOnly><StaffEditPage /></AdminOnly>} />
            <Route path="profile" element={<Profile />} />
            <Route path="help" element={<HelpPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
